"use client"

import React, { useState, useRef, useEffect } from 'react';
import { Camera, Edit2, Upload, Trash2 } from 'lucide-react';
import { replaceProfilePhoto, deleteProfilePhoto } from '../../services/profilePhotoService';
import { useEmployeeProfileStore } from '../../stores/employeeProfileStore';
import type { EmployeeDetails } from '../../stores/employeeProfileStore';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Button, ConfirmationDialog, AvatarEditor, UserAvatar } from '@t-one-internal/design-system';
import { compressImage } from '../../utils/imageCompression';

interface ProfilePhotoEditorProps {
  employee: EmployeeDetails;
  onUpdate?: (updates: Partial<EmployeeDetails>) => void;
  size?: 'small' | 'medium' | 'large';
  showInSection?: boolean;
}

export const ProfilePhotoEditor: React.FC<ProfilePhotoEditorProps> = ({
  employee,
  onUpdate,
  size = 'medium',
  showInSection = false
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [editorImageSrc, setEditorImageSrc] = useState<string | null>(null);
  const [croppedFile, setCroppedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewUrlRef = useRef<string | null>(null);
  const { updatePersonalInfo, selectedEmployee } = useEmployeeProfileStore();

  const currentPhotoUrl = employee.profilePicture || employee.profile_pic_url || null;
  const displayUrl = previewUrl || currentPhotoUrl;

  useEffect(() => {
    return () => {
      if (previewUrlRef.current && previewUrlRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      if (editorImageSrc && editorImageSrc.startsWith('blob:')) {
        URL.revokeObjectURL(editorImageSrc);
      }
    };
  }, []);

  useEffect(() => {
    if (previewUrlRef.current && previewUrlRef.current.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrlRef.current);
    }
    previewUrlRef.current = previewUrl;
  }, [previewUrl]);

  useEffect(() => {
    return () => {
      if (editorImageSrc && editorImageSrc.startsWith('blob:')) {
        URL.revokeObjectURL(editorImageSrc);
      }
    };
  }, [editorImageSrc]);

  useEffect(() => {
    if (previewUrlRef.current && previewUrlRef.current.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
    setCroppedFile(null);
    setEditorImageSrc(null);
    setShowEditor(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [employee.user_uuid]);

  const iconSizes = {
    small: 'w-4 h-4',
    medium: 'w-5 h-5',
    large: 'w-6 h-6'
  };

  const avatarSizeMap = {
    small: '2xl' as const,
    medium: '3xl' as const,
    large: '4xl' as const
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) { setError('No file selected'); return; }

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (JPEG, PNG, etc.)');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('Image size must be less than 10MB');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setIsCompressing(true);
    setError(null);

    try {
      const compressedFile = await compressImage(file, 800, 800, 0.8);
      const reader = new FileReader();
      reader.onerror = () => {
        setError('Failed to read image file');
        setIsCompressing(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      };
      reader.onloadend = () => {
        try {
          const imageUrl = reader.result as string;
          if (!imageUrl) throw new Error('Failed to load image');
          setEditorImageSrc(imageUrl);
          setShowEditor(true);
          setIsCompressing(false);
        } catch (err: any) {
          setError('Failed to process image. Please try another file.');
          setIsCompressing(false);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }
      };
      reader.readAsDataURL(compressedFile);

      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(compressedFile);
      if (fileInputRef.current) fileInputRef.current.files = dataTransfer.files;
    } catch (err: any) {
      setError(err.message || 'Failed to process image. Please try another file.');
      setIsCompressing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleUpload = async () => {
    const file = croppedFile || fileInputRef.current?.files?.[0];
    if (!file) { setError('Please select a file'); return; }
    if (!employee.user_uuid) { setError('User UUID is required'); return; }

    setIsUploading(true);
    setError(null);

    try {
      await replaceProfilePhoto(employee.user_uuid, file);

      if (previewUrlRef.current && previewUrlRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      setPreviewUrl(null);
      setCroppedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setIsModalOpen(false);

      if (selectedEmployee?.user_uuid) {
        await useEmployeeProfileStore.getState().refreshEmployeeDetails();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to upload profile photo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!employee.user_uuid) {
      setError('User UUID is required');
      setShowDeleteDialog(false);
      return;
    }

    setIsUploading(true);
    setError(null);
    setShowDeleteDialog(false);

    try {
      await deleteProfilePhoto(employee.user_uuid);
      const updates: Partial<EmployeeDetails> = { profilePicture: null, profile_pic_url: null };
      if (updatePersonalInfo) await updatePersonalInfo(updates);
      if (onUpdate) await onUpdate(updates);

      if (previewUrlRef.current && previewUrlRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      setPreviewUrl(null);
      setCroppedFile(null);
      setIsModalOpen(false);

      if (selectedEmployee?.user_uuid) {
        await useEmployeeProfileStore.getState().refreshEmployeeDetails();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to delete profile photo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    if (previewUrlRef.current && previewUrlRef.current.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    if (editorImageSrc && editorImageSrc.startsWith('blob:')) URL.revokeObjectURL(editorImageSrc);
    setPreviewUrl(null);
    setEditorImageSrc(null);
    setShowEditor(false);
    setCroppedFile(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleEditorSave = async (croppedBlob: Blob) => {
    try {
      const croppedFileObj = new File([croppedBlob], 'profile-photo.jpg', { type: 'image/jpeg', lastModified: Date.now() });
      const compressedFile = await compressImage(croppedFileObj, 400, 400, 0.85);
      setCroppedFile(compressedFile);

      if (previewUrlRef.current && previewUrlRef.current.startsWith('blob:')) URL.revokeObjectURL(previewUrlRef.current);
      const newPreviewUrl = URL.createObjectURL(compressedFile);
      setPreviewUrl(newPreviewUrl);
      setShowEditor(false);

      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(compressedFile);
      if (fileInputRef.current) fileInputRef.current.files = dataTransfer.files;
    } catch (err: any) {
      setError('Failed to save cropped image. Please try again.');
    }
  };

  const handleModalClose = () => {
    if (!isUploading) { setIsModalOpen(false); handleCancel(); }
  };

  if (showInSection) {
    return (
      <div className="w-full">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-slate-900">Profile Photo</h3>
        </div>
        <div className="flex flex-col items-center gap-4">
          <div className="relative border-2 border-slate-200 rounded-full">
            <UserAvatar name={employee.name || employee.firstName || 'Unknown'} userId={employee.user_uuid} imageUrl={displayUrl || undefined} size="3xl" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="relative inline-block cursor-pointer group" onClick={() => setIsModalOpen(true)}>
        <div className="relative">
          <div className="relative border-2 border-slate-200 rounded-full transition-opacity group-hover:opacity-75">
            <UserAvatar name={employee.name || employee.firstName || 'Unknown'} userId={employee.user_uuid} imageUrl={displayUrl || undefined} size={avatarSizeMap[size]} />
          </div>
          <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Edit2 className={`${iconSizes[size]} text-white`} />
          </div>
        </div>
      </div>

      <Dialog open={isModalOpen} onOpenChange={handleModalClose}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Profile Photo</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            {showEditor && editorImageSrc ? (
              <AvatarEditor imageSrc={editorImageSrc} onSave={handleEditorSave} onCancel={() => { setShowEditor(false); setEditorImageSrc(null); handleCancel(); }} shape="circle" />
            ) : (
              <>
                <div className="relative">
                  <div className="relative border-2 border-slate-200 rounded-full">
                    <UserAvatar name={employee.name || employee.firstName || 'Unknown'} userId={employee.user_uuid} imageUrl={displayUrl || undefined} size="3xl" />
                    {previewUrl && (
                      <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs">Preview</span>
                      </div>
                    )}
                  </div>
                </div>
                {isCompressing && (
                  <div className="text-sm text-blue-600 bg-blue-50 px-3 py-2 rounded-md w-full flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    Compressing image...
                  </div>
                )}
                {error && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-md w-full">{error}</div>}
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
                <div className="flex flex-col gap-2 w-full">
                  <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={isUploading || isCompressing} className="w-full">
                    <Upload className="w-4 h-4 mr-2" />
                    {displayUrl ? 'Replace Photo' : 'Upload Photo'}
                  </Button>
                  {previewUrl && !showEditor && (
                    <div className="flex gap-2">
                      <Button onClick={handleUpload} disabled={isUploading} className="flex-1">
                        {isUploading ? (<><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>Uploading...</>) : (<><Camera className="w-4 h-4 mr-2" />Save Photo</>)}
                      </Button>
                      <Button variant="outline" onClick={handleCancel} disabled={isUploading}>Cancel</Button>
                    </div>
                  )}
                  {currentPhotoUrl && !previewUrl && !showEditor && (
                    <Button variant="outline" onClick={() => setShowDeleteDialog(true)} disabled={isUploading} className="w-full text-red-600 border-red-300 hover:bg-red-50">
                      <Trash2 className="w-4 h-4 mr-2" />Delete Photo
                    </Button>
                  )}
                </div>
                <p className="text-xs text-slate-500 text-center">Images are automatically compressed for faster loading. Recommended: JPEG format, square image</p>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleModalClose} disabled={isUploading}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleDelete}
        title="Delete Profile Photo"
        description={`Are you sure you want to delete ${employee.name || 'this employee'}'s profile photo? This action cannot be undone.`}
        confirmText="Delete Photo"
        cancelText="Cancel"
        variant="destructive"
        isLoading={isUploading}
      />
    </>
  );
};
